import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PageHero from '../components/common/PageHero';
import PrimaryButton from '../components/common/PrimaryButton';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, FileText } from 'lucide-react';

const News = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const itemsPerPage = 8;
  const totalPages = Math.ceil(newsData.length / itemsPerPage);

  const paginatedNews = newsData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/news`);
      setNewsData(response.data || []);
    } catch (error) {
      console.error('Error fetching news:', error);
      setNewsData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date)) return '';
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <PageHero title="Latest News" />
        <div className="container mx-auto px-4 py-12 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <PageHero title="Latest News" />
      
      <div className="container mx-auto px-4 py-12">
            {/* News List */}
            {newsData.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No News Yet</h3>
                  <p className="text-gray-600">Check back later for updates.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {paginatedNews.map((news) => {
                const dateString = news.published_at || news.created_at || news.date;
                const date = dateString ? new Date(dateString) : new Date();
                const day = !isNaN(date) ? date.getDate() : '';
                const month = !isNaN(date) ? date.toLocaleString('en-US', { month: 'short' }) : '';
                
                return (
                  <Card key={news.id} className="overflow-hidden hover:shadow-lg transition-shadow border border-gray-200">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-[#2c3e7d] text-white rounded-lg p-4 text-center flex-shrink-0 min-w-[80px]">
                          <div className="text-3xl font-bold">{day}</div>
                          <div className="text-sm uppercase">{month}</div>
                        </div>
                        <div className="flex-1">
                          <Link to={`/news/${news.id}`}>
                            <h2 className="text-xl font-bold text-[#2c3e7d] mb-2 hover:text-[#3d4f8e] transition-colors">
                              {news.title}
                            </h2>
                          </Link>
                          <p className="text-sm text-gray-500 mb-3">{formatDate(dateString)}</p>
                          <div 
                            className="text-gray-700 leading-relaxed mb-4"
                            dangerouslySetInnerHTML={{
                              __html: (news.summary || news.excerpt || '').replace(
                                /(https?:\/\/[^\s]+)/g,
                                '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>'
                              )
                            }}
                          />
                          {news.attachments && news.attachments.length > 0 && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 mb-3">
                              <FileText size={16} />
                              <span>{news.attachments.length} attachment(s)</span>
                            </div>
                          )}
                          <Link to={`/news/${news.id}`}>
                            <button className="text-[#2c3e7d] hover:text-[#3d4f8e] font-medium text-sm underline">
                              Read More
                            </button>
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center items-center space-x-2">
                <PrimaryButton
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </PrimaryButton>
                {[...Array(totalPages)].map((_, index) => (
                  <PrimaryButton
                    key={index}
                    variant={currentPage === index + 1 ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </PrimaryButton>
                ))}
                <PrimaryButton
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </PrimaryButton>
              </div>
            )}
      </div>
    </div>
  );
};

export default News;